import React, { useState, useEffect, useRef, Fragment, Dispatch, SetStateAction } from 'react';
import { fabric } from 'sunzi-fabric';
import classnames from 'classnames';
import { Shop } from '@/sunzi';
import { i18n, loadImageFromURL, base64ToBlob, getToken7n, uuid, upload7nB64, cropImageTransparent } from '@/utils';
import { Header, Loading } from '@/components';
import Icon from './share-icon';
import ShareLinks from './share-links';
import styles from './share.less';

export enum ShareType {
  NotShare, // 未分享
  Shared, // 已分享
}

interface ShareProps {
  /** 商铺信息 */
  shop: Shop;
  /** 分享链接 */
  shareLink?: string;
  /** 分享文案 */
  shareText?: string;
  /** 分享优惠价格 */
  sharePrice?: number;
  /** 分享背景图 */
  shareBackgroundImage: string;
  /** 产品 */
  product?: string;
  /** 用户合成图片 */
  image: string;
  /** 商品价格 */
  price: number;
  /** 设置是否分享 */
  setIsShare: Dispatch<SetStateAction<boolean>>;
  /** 谷歌统计 */
  gtag: any;
  /** 确认回调 */
  onConfirm: () => void;
  /** 关闭回调 */
  onClose: () => void;
  /** 跳过回调 */
  onSkip: () => void;
}

const ShareDrawer: React.FC<ShareProps> = ({
  shop,
  shareLink,
  shareText,
  sharePrice,
  shareBackgroundImage,
  product,
  image,
  price,
  gtag,
  setIsShare,
  onConfirm,
  onClose,
  onSkip,
}) => {

  /** 分享图 blob url */
  const shareImageURL = useRef<string>('');
  /** 裁剪后卡通人图片 */
  const miniMeImageBase64 = useRef<string>('');
  /** 分享链接弹窗显影 */
  const [ shareLinksVisible, setShareLinksVisible ] = useState<boolean>(false);
  /** 分享状态 */
  const [ shareStatus, setShareStatus ] = useState<ShareType>(ShareType.NotShare);
  /** 分享图片 */
  const [ shareImage, setShareImage ] = useState<{ url: string, base64: string }>({
    url: '',
    base64: '',
  });
  /** 分享加载 */
  const [ shareLoading, setShareLoading ] = useState<boolean>(false);
  /** minime 图片生成加载 */
  const [ createImageLoading, setCreateImageLoading ] = useState<boolean>(true);
  /** 分享前价格 */
  // const originPrice = (parseFloat(price.slice(1))).toFixed(2);

  /** 生成无透明边的卡通图 */
  const createMiniMeImage = async () => {
    miniMeImageBase64.current = await cropImageTransparent(image);
    setCreateImageLoading(false);
  }

  /** 生成分享图 */
  const createShareImage = async () => {
    const element = document.createElement('canvas');
    const canvas = new fabric.Canvas(element);

    // 根据贺卡底图设置尺寸
    canvas.setDimensions({
      width: 1600,
      height: 900,
    });

    // 加载图片
    const backgroundImageBlobURL = URL.createObjectURL(base64ToBlob(shareBackgroundImage));
    const miniMeImageBlobURL = URL.createObjectURL(base64ToBlob(miniMeImageBase64.current));
    const [ backgroundImage, miniMeImage ] = await Promise.all([
      loadImageFromURL(backgroundImageBlobURL),
      loadImageFromURL(miniMeImageBlobURL),
    ]);
    URL.revokeObjectURL(backgroundImageBlobURL);
    URL.revokeObjectURL(miniMeImageBlobURL);

    // 画贺卡底图
    backgroundImage.set({
      left: 0,
      top: 0,
    });
    canvas.add(backgroundImage);

    // 画卡通人物图片
    const miniMeLeft = canvas.getWidth() / 2;
    const miniMeTop = 77;
    const miniMeHeight = canvas.getHeight() * 0.8;
    const miniMeWidth = canvas.getWidth() * 0.6;
    const miniMeScale = Math.min(
      miniMeHeight / (miniMeImage.height as number),
      miniMeWidth / (miniMeImage.width as number),
    );
    miniMeImage.set({
      left: miniMeLeft,
      top: miniMeTop + miniMeHeight,
      originX: 'center',
      originY: 'bottom',
      scaleX: miniMeScale,
      scaleY: miniMeScale,
    });
    canvas.add(miniMeImage);

    canvas.renderAll();
    const result = canvas.toDataURL({ format: 'jpeg' });
    canvas.clear();
    canvas.dispose();
    element.width = element.height = 0;
    return result;
  }

  /** 上传图片 */
  const uploadImage = async () => {
    const stime = new Date().getTime();
    const [ shareImage, token ] = await Promise.all([
      createShareImage(),
      getToken7n(),
    ]);
    const encodeKey = `new-sock/share/${uuid()}`;
    const response = await upload7nB64({ base64: shareImage, token, key: `${encodeKey}-share.png` });
    shareImageURL.current = host7n['prefix-na0'] + response.key;
    setShareImage({
      url: response.key.split('new-sock/share/')[1],
      base64: shareImage,
    });
    gtag('event', 'timing_complete', {
      'event_category': 'mini_me_share',
      'name': 'create_share_image',
      'value': new Date().getTime() - stime,
    });
  }

  /** 分享跳过 */
  const handleSkip = () => {
    gtag('event', 'sunzi_share_skip', {
      'event_category': 'mini_me_share'
    });
    onSkip();
  }

  /** 点击下载贺卡 */
  const handleGreetingCardDownload = () => {
    gtag('event', 'sunzi_share_download_greeting_card', {
      'event_category': 'mini_me_share'
    });
  }

  useEffect(() => {
    if (shareLinksVisible === true && !shareImage.url) {
      setTimeout(uploadImage, 320);
      gtag('event', 'sunzi_share_open', {
        'event_category': 'mini_me_share'
      });
    }
  }, [ shareLinksVisible ]);

  useEffect(() => {
    if (shareStatus === ShareType.Shared) {
      setIsShare(true);
    }
  }, [ shareStatus ]);

  useEffect(() => {
    createMiniMeImage();
  }, []);

  return (
    <div className={classnames(styles.share, {
      [styles.disableEvent]: shareLoading,
    })}>
      <Header
        className={styles.header}
        closeIconType="down"
        closeIconColor="#000"
        onClose={onClose}
      />
        <div className={classnames(styles.title, {
          [styles.shared]: shareStatus === ShareType.Shared,
        })}>
          {shareStatus === ShareType.NotShare ? i18n.format('modules.mini.me.share.title') : i18n.format('modules.mini.me.shared.title')}
        </div>
        <div className={styles.shareContent}>
          <div className={styles.shareImageWarpper}>
            {createImageLoading &&
              <div className={styles.imageLoading}>
                <Loading color="#ff533a" />
              </div>
            }
            <img className={styles.backgroundImage} src={shareBackgroundImage} />
            <img className={styles.miniMeImage} src={miniMeImageBase64.current} />
          </div>
        </div>
        {shareStatus === ShareType.NotShare ?
          <div className={styles.shareTip}>
            {i18n.format('modules.mini.me.share.tip', `${shop.currencySymbol}${sharePrice}`)}
            {/* <span className={styles.shareTipOff}>{` ${i18n.format('modules.mini.me.discount.price', `${shop.currencySymbol}${sharePrice}`)}`}</span> */}
          </div> :
          <Fragment>
            <div className={styles.sharedTip}>
              <div className={styles.text}>{i18n.format('modules.mini.me.shared.card.discount', `${shop.currencySymbol}${sharePrice}`)}</div>
              <div className={styles.price}>
                <div className={styles.nowPrice}>{`${shop.currencySymbol}${(price - sharePrice!).toFixed(2)}`}</div>
                <div className={styles.oldPrice}>{`${shop.currencySymbol}${price.toFixed(2)}`}</div>
              </div>
            </div>
            <a
              target="_blank"
              href={shareImageURL.current}
              className={styles.download}
              onClick={handleGreetingCardDownload}
            >
              <Icon type="download" color="#ff533a" size={24} />
              <span className={styles.downloadText}>{i18n.format('modules.mini.me.download.card')}</span>
            </a>
          </Fragment>
        }
        {shareStatus === ShareType.NotShare ?
          <div className={styles.shareButtonLayout}>
            <div
              className={styles.buttonShare}
              onClick={() => setShareLinksVisible(true)}
            >{i18n.format('modules.mini.me.share.now')}</div>
            <div
              className={styles.buttonWithout}
              onClick={handleSkip}
            >
              {i18n.format('modules.mini.me.no.thanks')}
            </div>
          </div> :
          <div
            className={styles.addCartButton}
            onClick={onConfirm}
          >
            {i18n.format('modules.global.add.cart')}
          </div>
        }
      {shareLinksVisible &&
        <ShareLinks
          shareLink={shareLink}
          shareImage={shareImage}
          shareText={shareText!}
          product={product}
          gtag={gtag}
          setShareStatus={setShareStatus}
          setShareLoading={setShareLoading}
          onClose={() => setShareLinksVisible(false)}
        />
      }
      {shareLoading &&
        <div className={styles.shareLoading}>
          <Loading color="#fff" size={50} />
        </div>
      }
    </div>
  )
}

export default ShareDrawer;
